import { Signal } from '../utilities/async/Signal';
import { resolveItems, supportFacets } from './getItems';
import { waitForNextStoreUpdate } from '../store/enhancers/NextStoreUpdateEnhancer';
import { logPageLoad } from './logPageLoad';
import { now, mark } from '../utilities/performance';
import { Facet } from '../models/Facet';
import { KeyFacet } from '../models/KeyFacet';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { IOnCurrentItemKeyChangedProps } from '../components/itemsNavigator/ItemsNavigator';
import { registerPermanentItemDemands } from './pruneItems';
import { readItems } from './readItems';
import { IItemCache } from '../models/IItemCacheObserver';
import {
    pageLoadConfigurationFacet,
    hasPageLoadedFacet,
    lastNavigationEventFacet,
    itemsScopeInitTimeFacet,
    resetPerfLoggerKeyFacet,
    resetPerfLoggerFacet,
    loggerFacet
} from '../facets/LoggerFacets';
import { configurationItemKey } from '../facets/ConfigurationFacets';
import { createAddon } from '../addons/registerOperationHandler';
import { readConfiguration } from './configuration/readConfiguration';
import { clearItems } from './clearItems';
import { CancelError } from '../utilities/async/CancelError';
import { invalidateItems } from './invalidateItems';

const pageLoadKeyFacet = new KeyFacet<{}>('pageLoad', {});

const pageLoadKey = pageLoadKeyFacet.serialize({});

/**
 * The item key on which page-load state is tracked.
 * @internal
 */
export const pageLoadItemKey: string = pageLoadKey;

const pageLoadSignalFacet = new Facet<Signal<void>>('pageLoadSignal');

const pageLoadAddon = createAddon(() => {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(supportFacets({
            keyFacet: pageLoadKeyFacet,
            facets: []
        }));
    };
});

function getIsPageLoadBarrierEnabled(): IItemCacheThunkAction<boolean> {
    return (dispatch: IItemCacheDispatch) => {
        const {
            pageLoadConfiguration
        } = dispatch(readConfiguration({
            pageLoadConfiguration: pageLoadConfigurationFacet
        }));

        const {
            enablePageLoadBarrier = false
        } = pageLoadConfiguration || {};

        return enablePageLoadBarrier;
    };
}

/**
 * @internal
 */
export function isPageLoaded(itemCache: IItemCache): boolean {
    const pageLoadConfiguration = itemCache.demandItemFacet(pageLoadConfigurationFacet, configurationItemKey);

    const {
        enablePageLoadBarrier = false
    } = pageLoadConfiguration || {};

    return !enablePageLoadBarrier || !!itemCache.demandItemFacet(hasPageLoadedFacet, pageLoadKey);
}

/**
 * @public
 */
export function getHasPageLoaded(): IItemCacheThunkAction<boolean> {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        return dispatch(readItems(isPageLoaded));
    };
}

/**
 * @internal
 */
export function getPageLoadBarrier(itemCache: IItemCache): Promise<void> | undefined {
    const pageLoadConfiguration = itemCache.demandItemFacet(pageLoadConfigurationFacet, configurationItemKey);

    const {
        enablePageLoadBarrier = false
    } = pageLoadConfiguration || {};

    if (enablePageLoadBarrier) {
        const pageLoadSignal = itemCache.demandItemFacet(pageLoadSignalFacet, pageLoadKey);

        if (pageLoadSignal) {
            return pageLoadSignal.promise;
        }
    }

    return undefined;
}

/**
 * @internal
 */
export function signalItemsViewRenderComplete(itemsScopeItemKey: string, pageLoadTime?: number): IItemCacheThunkAction<void> {
    return async (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        const {
            pageLoadSignal,
            hasPageLoaded,
            itemsViewRenderCompleteInfo,
            logger,
            pageLoadConfiguration
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                pageLoadSignal: itemCache.demandItemFacet(pageLoadSignalFacet, pageLoadKey),
                hasPageLoaded: itemCache.demandItemFacet(hasPageLoadedFacet, pageLoadKey),
                itemsViewRenderCompleteInfo: itemCache.demandItemFacet(lastNavigationEventFacet, itemsScopeItemKey),
                logger: itemCache.demandItemFacet(loggerFacet, configurationItemKey),
                pageLoadConfiguration: itemCache.demandItemFacet(pageLoadConfigurationFacet, configurationItemKey)
            };
        }));

        const {
            renderComplete = false,
            keyChanges = undefined
        } = itemsViewRenderCompleteInfo || {};

        const {
            continueLoggingAfterPLT = false
        } = logger || {};

        const {
            pageLoadReadySignal = undefined,
            pageLoadContinueSignal = undefined
        } = pageLoadConfiguration || {};

        // Collect timestamp if we haven't report render complete yet
        let timestamp = (continueLoggingAfterPLT || !renderComplete) ? (pageLoadTime ? pageLoadTime : now()) : undefined;
        const {
            ScopeInitTime = NaN
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                ScopeInitTime: itemCache.demandItemFacet(itemsScopeInitTimeFacet, itemsScopeItemKey)
            };
        }));

        if (timestamp) {
            dispatch(logPageLoad(timestamp, {
                isFullPageLoad: !hasPageLoaded,
                keyChanges: keyChanges,
                itemsScopeItemKey: itemsScopeItemKey,
                itemsScopeInitTime: ScopeInitTime
            }));
        }

        let signal: Signal<void>;

        if (pageLoadSignal) {
            signal = pageLoadSignal;
        } else {
            signal = new Signal<void>();
        }

        if (pageLoadReadySignal) {
            pageLoadReadySignal.resolve();
        }

        if (pageLoadContinueSignal) {
            await pageLoadContinueSignal.promise;
        }

        dispatch(invalidateItems({
            items: {
                [pageLoadKey]: [hasPageLoadedFacet]
            }
        }));

        dispatch(resolveItems({
            items: {
                [itemsScopeItemKey]: {
                    ...lastNavigationEventFacet.pack({
                        renderComplete: true,
                        keyChanges: keyChanges
                    })
                },
                [pageLoadKey]: {
                    ...pageLoadSignalFacet.pack(signal),
                    ...hasPageLoadedFacet.pack(true)
                }
            }
        }));

        dispatch(registerPermanentItemDemands({
            items: {
                [pageLoadKey]: {}
            }
        }));

        dispatch(waitForNextStoreUpdate()).then(() => signal.resolve());
    };
}

/**
 * @internal
 */
export function signalItemsViewNavigationStart(payload: {
    itemsScopeItemKey: string,
} & IOnCurrentItemKeyChangedProps): IItemCacheThunkAction<void> {
    const {
        itemsScopeItemKey,
        ...itemKeyChange
    } = payload;

    return (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        mark(`signalItemsViweNavigationStart_key(${itemsScopeItemKey})`);

        const resetPerfLoggerKey = resetPerfLoggerKeyFacet.serialize({});

        const {
            hasPageLoaded,
            resetPerfLogger
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                hasPageLoaded: itemCache.demandItemFacet(hasPageLoadedFacet, pageLoadKey),
                resetPerfLogger: itemCache.demandItemFacet(resetPerfLoggerFacet, resetPerfLoggerKey)
            };
        }));

        if (resetPerfLogger) {
            resetPerfLogger({
                isFullPageLoad: !hasPageLoaded,
                keyChanges: itemKeyChange,
                itemsScopeItemKey: itemsScopeItemKey
            });
        }

        dispatch(resolveItems({
            items: {
                [itemsScopeItemKey]: {
                    ...lastNavigationEventFacet.pack({
                        renderComplete: false,
                        keyChanges: itemKeyChange
                    })
                }
            }
        }));
    };
}

/**
 * @public
 */
export interface IWaitForPageLoadPayload {
    /**
     * Whether or not to wait indefinitely, even if the page fails to load or is canceled.
     *
     * @remarks
     * Use this flag only within locations which will only be invoked once per-application, such
     * as when waiting for page load before loading and dispatching a deferred addon.
     *
     * Do not use this flag within asynchronous callbacks for deferred operation handlers, control handlers,
     * or asynchronously-loaded components, as these can be retried by their callers in response to a page
     * load failure.
     */
    waitIndefinitely?: boolean;
}

/**
 * Creates an action which, when dispatched, waits for a 'page load' state signal
 * from the cache, which can be set by various UX components to indicate completion of rendering.
 * @public
 */
export function waitForPageLoad(payload: IWaitForPageLoadPayload = {}): IItemCacheThunkAction<Promise<void>> {
    const {
        waitIndefinitely
    } = payload;

    return async (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        let loaded = false;

        while (!loaded) {
            try {
                const enablePageLoadBarrier = dispatch(getIsPageLoadBarrierEnabled());

                if (!enablePageLoadBarrier) {
                    return;
                }

                const pageLoadSignal = dispatch(readItems((
                    itemCache: IItemCache
                ) => itemCache.demandItemFacet(pageLoadSignalFacet, pageLoadKey)));

                let signal: Signal<void>;

                if (pageLoadSignal) {
                    signal = pageLoadSignal;
                } else {
                    signal = new Signal<void>();

                    dispatch(resolveItems({
                        items: {
                            [pageLoadKey]: {
                                ...pageLoadSignalFacet.pack(signal)
                            }
                        }
                    }));

                    registerPermanentItemDemands({
                        items: {
                            [pageLoadKey]: {}
                        }
                    });
                }

                await signal.promise;

                loaded = true;
            } catch (e) {
                if (!waitIndefinitely || !(e instanceof CancelError)) {
                    throw e;
                }
            }
        }
    };
}

/**
 * Waits for the 'ready' signal to be fired by the item cache, in case a host application
 * wishes to delay the final 'page load' event until after an additional circumstance.
 * @public
 */
export function waitForPageLoadReady(): IItemCacheThunkAction<Promise<void>> {
    return async (dispatch: IItemCacheDispatch) => {
        const pageLoadConfiguration = dispatch(readItems((itemCache: IItemCache) =>
            itemCache.demandItemFacet(pageLoadConfigurationFacet, configurationItemKey)));

        const {
            pageLoadReadySignal = undefined
        } = pageLoadConfiguration || {};

        if (!pageLoadReadySignal) {
            return;
        }

        await pageLoadReadySignal.promise;
    };
}

/**
 * Creates an action which, when dispatched, resets the page load state
 * if the page has been loaded.
 * @beta
 *
 * @remarks
 * This function will cancel all operations that are caught waiting on 'page load',
 * which will require them to be retried.
 */
export function resetPageLoad(): IItemCacheThunkAction<void> {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        const enablePageLoadBarrier = dispatch(getIsPageLoadBarrierEnabled());

        if (!enablePageLoadBarrier) {
            return;
        }

        const pageLoadSignal = dispatch(readItems((itemCache: IItemCache) => {
            return itemCache.demandItemFacet(pageLoadSignalFacet, pageLoadKey);
        }));

        dispatch(clearItems({
            items: {
                [pageLoadKey]: true
            }
        }));

        if (pageLoadSignal) {
            // If there was already a signal registered, mark is as canceled so subscribers can
            // stop waiting and resubscribe to a new wait signal.
            dispatch(waitForNextStoreUpdate()).then(() => pageLoadSignal.reject(new CancelError({
                message: 'Reset page load state.'
            })));
        }
    };
}
